import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';

const SubmitFeedbackModal = ({
  open,
  onClose,
  onConfirm,
  formData,
  reportNumber,
  leftInference,
  rightInference,
  leftDME,
  rightDME
}) => {
  // Display mapping for better user experience
  const getDisplayText = (option) => {
    // No mapping needed since we're using the display text as the actual value
    return option;
  };

  // Function to get the display text for inference (handles the transformation)
  const getInferenceDisplayText = (inference) => {
    // No mapping needed since we're using consistent values
    return inference;
  };

  const getDMEDisplayText = (dme) =>
    dme === "DME" ? "Diabetic Macular Edema (DME) present" : "Diabetic Macular Edema (DME) absent";

  // Generic eye-section renderer, shared by both the DR and DME confirmation blocks below -
  // caller passes in already display-formatted values.
  const renderEyeSection = (eye, sectionLabel, displayValue, isCorrect, correctedDisplayValue) => (
    <Box sx={{ marginBottom: '20px' }}>

      {/* Eye label + Correct/Incorrect badge in the same row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <Typography
          variant="body1"
          sx={{
            color: '#666',
            fontWeight: 500,
            fontSize: '14px',
            marginRight: '6px',
          }}
        >
          {sectionLabel}
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '6px 14px',
            borderRadius: '6px',
            backgroundColor: isCorrect ? '#d4edda' : '#f8d7da',
            border: `1px solid ${isCorrect ? '#c3e6cb' : '#f5c6cb'}`,
          }}
        >
          {isCorrect ? (
            <CheckIcon sx={{ fontSize: 20, color: '#155724', marginRight: '6px' }} />
          ) : (
            <ClearIcon sx={{ fontSize: 20, color: '#721c24', marginRight: '6px' }} />
          )}
          <Typography
            sx={{
              color: isCorrect ? '#155724' : '#721c24',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            {isCorrect ? 'Correct' : 'Incorrect'}
          </Typography>
        </Box>
      </Box>

      {/* Inference */}
      <Typography
        variant="body1"
        sx={{
          color: '#D2691E',
          fontWeight: 500,
          fontSize: '16px',
          marginBottom: '8px'
        }}
      >
        {displayValue}
      </Typography>

      {/* Correction suggestion */}
      {!isCorrect && correctedDisplayValue && (
        <Typography
          variant="body2"
          sx={{
            backgroundColor: '#F9FCFF',
            color: '#0D1E83',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: 1.4,
            textAlign: 'center',
            borderRadius: '4px',
            padding: '8px',
            maxWidth: 'fit-content',
            margin: '0 auto',
          }}
        >
          You have suggested {correctedDisplayValue} in {eye.toLowerCase()} eye
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '0px',
          width: '420px',
          minHeight: '380px',
          maxWidth: 'none',
          maxHeight: 'none'
        }
      }}
    >
      <DialogTitle sx={{ padding: '20px 24px 12px 24px', position: 'relative' }}>
        <Typography
          component="div"
          sx={{
            color: '#051572',
            fontWeight: 600,
            fontSize: '20px',
            textAlign: 'center',
            paddingRight: '32px'
          }}
        >
          Submit Feedback
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            color: '#051572',
            padding: '6px'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '0px 24px 16px 24px' }}>
        <Typography
          variant="body1"
          sx={{
            marginBottom: '24px',
            color: '#666',
            fontSize: '14px',
            lineHeight: 1.5,
            fontWeight: 400
          }}
        >
          Are you sure you want to submit the following feedback from the AI Inference of{' '}
          <strong>Report {reportNumber}</strong> :
        </Typography>

        {/* Right Eye - DME then DR, only shown if that eye's confirmation was captured */}
        {formData?.rightEyeDMECorrect && renderEyeSection(
          'Right',
          'Right Eye - DME',
          getDMEDisplayText(rightDME),
          formData.rightEyeDMECorrect === 'yes',
          formData.rightEyeCorrectedDME && getDMEDisplayText(formData.rightEyeCorrectedDME)
        )}
        {formData?.rightEyeCorrect && renderEyeSection(
          'Right',
          'Right Eye - DR',
          getInferenceDisplayText(rightInference),
          formData.rightEyeCorrect === 'yes',
          formData.rightEyeCorrectedInference && getDisplayText(formData.rightEyeCorrectedInference)
        )}

        {/* Divider between eyes - only if the right eye had any feedback captured */}
        {(formData?.rightEyeDMECorrect || formData?.rightEyeCorrect) &&
          (formData?.leftEyeDMECorrect || formData?.leftEyeCorrect) && (
            <Divider sx={{ marginY: 2 }} />
        )}

        {/* Left Eye - DME then DR */}
        {formData?.leftEyeDMECorrect && renderEyeSection(
          'Left',
          'Left Eye - DME',
          getDMEDisplayText(leftDME),
          formData.leftEyeDMECorrect === 'yes',
          formData.leftEyeCorrectedDME && getDMEDisplayText(formData.leftEyeCorrectedDME)
        )}
        {formData?.leftEyeCorrect && renderEyeSection(
          'Left',
          'Left Eye - DR',
          getInferenceDisplayText(leftInference),
          formData.leftEyeCorrect === 'yes',
          formData.leftEyeCorrectedInference && getDisplayText(formData.leftEyeCorrectedInference)
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px 24px 24px', gap: '12px', justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: '#C40320',
            color: '#C40320',
            borderRadius: '8px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            minWidth: '100px',
            '&:hover': {
              borderColor: '#C40320',
              backgroundColor: 'rgba(196, 3, 32, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            backgroundColor: '#051572',
            borderRadius: '8px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            minWidth: '100px',
            '&:hover': {
              backgroundColor: '#051572',
            },
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitFeedbackModal;
